<template>
  <div v-has-any-perms="rolePerm">
    <portal-target
      v-show="showList"
      name="layout-main"
      class="layout-main panel-scroll-full"
    >
      <div class="panel-list panel-fixed">
        <div class="panel-header">
          <h1 class="page-header mr-2 d-flex align-items-center">
            {{ titleWithOrganizationName | translate }}
          </h1>
          <ol class="breadcrumb pull-right">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'dashboard'}">
                {{"Dashboard"}}
              </router-link>
            </li>
            <li v-for="(breadcrumb) in breadcrumbs" :key="breadcrumb.label" class="breadcrumb-item">
              <router-link :to="breadcrumb.route">
                {{ breadcrumb.label | translate }}
              </router-link>
            </li>
            <li class="breadcrumb-item">{{ title | translate }}</li>
          </ol>
        </div>
        <advanced-search
          v-show="showAdvancedSearch"
          v-if="toggleAdvancedSearch"
          :fields="searchFields"
          :reset="reset"
          @search="addFiltersToInstance"
          @reset="removeFiltersToInstance"
          @active="activeFilters = $event"
        />
        <panel :title="title" :no-button="true">
          <thux-list-component
            :fields="getFields"
            :pagination="getPaginationFields"
            @select-per-page="changePerPage"
            @change-page="changePage"
            :action-select="getActionSelectFields"
            @select-action="setAction"
            @select-or-deselect-all-queryset="selectOrDeselectAllQueryset"
            @do-action="doAction($event, fieldName)"
            :advanced-search="getAdvancedSearchFields"
            @show-advanced-search="openAdvancedSearch"
            @open-edit-form="openEditForm()"
            @open-detail-form="openDetailForm()"
          >
            <template slot="other-headers-buttons">
              <b-btn
                variant="outline-primary"
                class="ml-2"
                @click.prevent="downloadXLSX()"
              >
                <i class="fas fa-file-excel" :alt="'Download Excel' | translate" />
                {{ 'Download Excel' | translate }}
              </b-btn>
              <b-btn
                variant="outline-primary"
                class="ml-2"
                @click.prevent="downloadPDF()"
              >
                <i class="fas fa-file-pdf" :alt="'Download PDF' | translate" />
                {{ 'Download PDF' | translate }}
              </b-btn>
            </template>
            <template slot="body">
              <component-table
                :list="list.results"
                :goBack="goBack"
                :params="tableParams"
                @open-edit-form="openEditForm"
                @open-detail-form="openDetailForm"
                @show-attachments="showOrganizationInspectionAttachments"
                @refresh-list="refreshListAfterSubmit"
              />
            </template>
          </thux-list-component>
        </panel>
      </div>
    </portal-target>
    <router-view />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { ROUTES as ORGANIZATION_ROUTES } from '../router'
import { TYPES as ORGANIZATION_TYPES } from '../organization/store'
import { ORGANIZATION_CUSTOMER_CODE, ORGANIZATION_SUPPLIER_CODE, ORGANIZATION_CUSTOMER_LEAD_CODE } from '@/const'

import ThuxListMixin from '../../../components/thux-list/ThuxListMixin'

import OrganizationSecurityRoleTable from './OrganizationSecurityRoleTable'
import { ROUTES as CUSTOMER_ORG_ROUTES } from '../organization/organization/customer-organization/router'
import { ROUTES as OWNER_ORG_ROUTES } from '../organization/organization/owner-organization/router'
import { ROUTES as LEAD_ORG_ROUTES } from '../organization/organization/lead-organization/router'
import { ROUTES as SUPPLIER_ORG_ROUTES } from '../organization/organization/supplier-organization/router'
import { TYPES as ORGANIZATION_CNT_SECURITY_ROLE_TYPES } from './store'
export default {
  name: 'OrganizationInspectionsList',
  mixins: [ThuxListMixin],
  components: {
    'component-table': OrganizationSecurityRoleTable
  },
  data () {
    return {
      ORGANIZATION_ROUTES,
      title: this.$t('Security roles'),
      rolePerm: ['organization_organizationinspection_list', 'organization_organizationcontactsecurityrole_list'],
      actionEnablePermission: ['organization_organizationinspection_enable'],
      actionDisablePermission: ['organization_organizationinspection_disable'],
      inspection: {},
      orgType: this.$route.params.orgType,
      showCommonFilters: false,
      breadcrumbs: [],
      showAttachments: false,
      searchFields: [
        { key: 'filter__contact__last_name__icontains', placeholder: this.$t('Last name'), type: 'text', col: 6 },
        { key: 'filter__contact__first_name__icontains', placeholder: this.$t('First name'), type: 'text', col: 6 },
        { key: 'filter__role__name__icontains', placeholder: this.$t('Contact Security Role'), type: 'text', col: 6 }
      ]
    }
  },
  created () {
    this.organizationRetrieve({ id: this.$route.params.id })
    this.setBreadcrumbs()
  },
  computed: {
    ...mapGetters({
      ...ORGANIZATION_CNT_SECURITY_ROLE_TYPES.GENERIC.organization.organizationcontactsecurityrole.LIST.GETTERS,
      organizationDetail: ORGANIZATION_TYPES.GENERIC.organization.organization.DETAIL.GETTERS.detail
    }),
    titleWithOrganizationName () {
      if (this.organizationDetail && this.organizationDetail.code) {
        return `${this.$t('Security roles')} ${this.organizationDetail.name ? this.organizationDetail.name : this.organizationDetail.customer_name}`
      }
      if (this.list && this.list.results && this.list.results.length > 0) {
        const organizationName = this.list.results[0].organization_name ? `(${this.list.results[0].organization_name})` : ''
        return `${this.$t('Security roles')} ${organizationName}`
      }
      return this.$t('Security roles')
    },
    showList () {
      return true
    },
    canSeeActionSelect () {
      return false
    },
    canAddInstance () {
      return false
    }
  },
  methods: {
    ...mapMutations({
      setSelectedList: ORGANIZATION_CNT_SECURITY_ROLE_TYPES.GENERIC.organization.organizationcontactsecurityrole.LIST.MUTATIONS.setSelectedList,
      setSelectAll: ORGANIZATION_CNT_SECURITY_ROLE_TYPES.GENERIC.organization.organizationcontactsecurityrole.LIST.MUTATIONS.setSelectAll,
      setOrderBy: ORGANIZATION_CNT_SECURITY_ROLE_TYPES.GENERIC.organization.organizationcontactsecurityrole.LIST.MUTATIONS.setOrderBy
    }),
    ...mapActions({
      ...ORGANIZATION_CNT_SECURITY_ROLE_TYPES.GENERIC.organization.organizationcontactsecurityrole.LIST.ACTIONS,
      organizationRetrieve: ORGANIZATION_TYPES.GENERIC.organization.organization.DETAIL.ACTIONS.retrieve
    }),
    openEditForm (id) {
      this.$set(this, 'componentId', id)
      this.$set(this, 'inspection', { id: id })
      if (id) {
        this.$set(this, 'showEditForm', true)
        this.$set(this, 'showAttachments', true)
        this.$set(this, 'showDetailForm', false)
      } else {
        this.$set(this, 'showEditForm', true)
        this.$set(this, 'showAttachments', false)
        this.$set(this, 'showDetailForm', false)
      }
    },
    setBreadcrumbs () {
      if (this.orgType === ORGANIZATION_SUPPLIER_CODE) {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organizations',
            route: {
              name: SUPPLIER_ORG_ROUTES.SUPPLIER_ORGANIZATION_LIST,
              // : CUSTOMER_ORG_ROUTES.CUSTOMER_ORGANIZATION_LIST,
              params: { orgType: ORGANIZATION_SUPPLIER_CODE }
            }
          },
          {
            label: 'Organization',
            route: {
              name: SUPPLIER_ORG_ROUTES.SUPPLIER_ORGANIZATION_DETAIL,
              params: {
                orgType: ORGANIZATION_SUPPLIER_CODE,
                id: this.$route.params.id
              }
            }
          }
        ])
      } else if (this.orgType === ORGANIZATION_CUSTOMER_LEAD_CODE) {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organizations',
            route: {
              name: LEAD_ORG_ROUTES.LEAD_ORGANIZATION_LIST,
              params: { orgType: ORGANIZATION_CUSTOMER_LEAD_CODE }
            }
          },
          {
            label: 'Organization',
            route: {
              name: LEAD_ORG_ROUTES.LEAD_ORGANIZATION_DETAIL,
              params: {
                orgType: ORGANIZATION_CUSTOMER_LEAD_CODE,
                id: this.$route.params.id
              }
            }
          }
        ])
      } else if (this.orgType === ORGANIZATION_CUSTOMER_CODE) {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organizations',
            route: {
              name: CUSTOMER_ORG_ROUTES.CUSTOMER_ORGANIZATION_LIST,
              params: { orgType: ORGANIZATION_CUSTOMER_CODE }
            }
          },
          {
            label: 'Organization',
            route: {
              name: CUSTOMER_ORG_ROUTES.CUSTOMER_ORGANIZATION_DETAIL,
              params: {
                orgType: ORGANIZATION_CUSTOMER_CODE,
                id: this.$route.params.id
              }
            }
          }
        ])
      } else {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organization',
            route: {
              name: OWNER_ORG_ROUTES.OWNER_ORGANIZATION_DETAIL,
              params: {
                id: this.$route.params.id
              }
            }
          }
        ])
      }
    },
    getComponentList () {
      this.cleanAllData()
      this.setOrderBy('contact__last_name')
      this.setFilters({
        filter__organization_id: this.$route.params.id,
        filter__contact__organization_contacts__organization_id: this.$route.params.id,
        filter__contact__organization_contacts__status: 1
      })
    },
    showOrganizationInspectionAttachments (inspection) {
      this.$set(this, 'inspection', inspection)
      this.$set(this, 'showAttachments', true)
      if (this.componentId !== inspection.id) {
        this.$set(this, 'showEditForm', false)
        this.$set(this, 'showDetailForm', false)
      }
    },
    refreshListAfterSubmit () {
      this.getComponentList()
      this.closeEditForm()
    },
    closeEditForm () {
      this.$set(this, 'showDetailForm', false)
      this.$set(this, 'showEditForm', false)
      this.$set(this, 'showAttachments', false)
    },
    downloadPDF () {
      const orgName = this.organizationDetail && (this.organizationDetail.name || this.organizationDetail.customer_name)
      const fileName = orgName ? `${orgName}_security_roles.pdf` : 'security_roles.pdf'
      this.downloadListPDF({ orgId: this.$route.params.id, simpleFileName: fileName })
    },
    downloadXLSX () {
      const orgName = this.organizationDetail && (this.organizationDetail.name || this.organizationDetail.customer_name)
      const fileName = orgName ? `${orgName}_security_roles.xlsx` : 'security_roles.xlsx'
      this.downloadListXLSX({ orgId: this.$route.params.id, simpleFileName: fileName })
    }
  }
}
</script>
